import { Link } from '@farol-ds/react'
import { COMPONENT_NAME, SELECTED_OPTION } from '@jusbrasil-web/shared-ga4-events'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { formatDate } from '@jusbrasil-web/shared-utils-format'

import emailImgSrc from '../../assets/cpf-validation/email.svg'
import errorImgSrc from '../../assets/cpf-validation/error.svg'
import successImgSrc from '../../assets/cpf-validation/success.svg'
import { CLICK_EVENT_PROPS, COMPONENT_RENDER_EVENT_PROPS, GA4EventHandlers } from '../../hooks'
import { CpfValidation_ViewerFragment } from './__generated__/cpf-validation.graphql'
import { SUPPORT_CONTACT_URL } from './constants'
import { SharedDataInterface } from './context'
import { Steps } from './enums'
import { CPFInUse, ReplacingCPF, StaticStep, ValidateIdentity, ValidatingIdentity } from './steps'
import { CpfValidationEventData, CpfValidationTexts } from './types'

interface StepFuncProps {
  viewer: CpfValidation_ViewerFragment | null
  onClose?: () => void
  sharedData?: SharedDataInterface
  eventData?: CpfValidationEventData
  stepsCallback?: (isValidatedSuccess: boolean) => void
  gA4EventHandlers?: GA4EventHandlers
  texts?: CpfValidationTexts
}

type StepFunc = (props: StepFuncProps) => React.ReactNode

export const FLOW_STEPS: Record<Steps, StepFunc> = {
  [Steps.VALIDATE_IDENTITY]: ({ gA4EventHandlers }) => {
    gA4EventHandlers?.sendComponentRender({
      componentClass: null,
      componentName: COMPONENT_NAME.IDENTITY_VALIDATION,
      renders: COMPONENT_RENDER_EVENT_PROPS.VALIDATE_IDENTITY,
    })
    return <ValidateIdentity />
  },
  [Steps.VALIDATING_IDENTITY]: ({ stepsCallback, gA4EventHandlers }) => (
    <ValidatingIdentity validateCallback={stepsCallback} gA4EventHandlers={gA4EventHandlers} />
  ),
  [Steps.IDENTITY_ERROR]: ({ gA4EventHandlers }) => {
    gA4EventHandlers?.sendComponentRender({
      componentClass: null,
      componentName: COMPONENT_NAME.IDENTITY_VALIDATION,
      renders: COMPONENT_RENDER_EVENT_PROPS.IDENTITY_ERROR,
    })

    return (
      <StaticStep
        testId="identity-error-step"
        imageSrc={errorImgSrc}
        title="Identidade não pôde ser verificada"
        bodyText={
          <>
            Por favor, revise suas informações ou entre em{' '}
            <Link
              href={SUPPORT_CONTACT_URL}
              target="_blank"
              onClick={() => gA4EventHandlers?.sendClick(CLICK_EVENT_PROPS.IDENTITY_ERROR)}
            >
              contato com o suporte
            </Link>
            .
          </>
        }
        action={{
          nextStep: Steps.VALIDATE_IDENTITY,
          label: 'Revisar Informações',
        }}
        onLoadEvent={{
          name: 'UserAccount.IdValidationFailedViewed',
          data: { type: 'CPFInvalid' },
        }}
      />
    )
  },
  [Steps.IDENTITY_SUCCESS]: ({ onClose, gA4EventHandlers, texts }) => {
    const title = texts?.identitySuccess?.title ?? 'Identidade verificada'
    const bodyText = texts?.identitySuccess?.bodyText ?? 'Seu CPF foi verificado com sucesso.'
    const buttonText = texts?.identitySuccess?.buttonText ?? 'Continuar'

    gA4EventHandlers?.sendComponentRender({
      componentClass: null,
      componentName: COMPONENT_NAME.IDENTITY_VALIDATION,
      renders: COMPONENT_RENDER_EVENT_PROPS.IDENTITY_SUCCESS,
    })

    return (
      <StaticStep
        testId="identity-success-step"
        imageSrc={successImgSrc}
        title={title}
        bodyText={bodyText}
        action={{
          onClick: onClose,
          label: buttonText,
        }}
        onLoadEvent={{
          name: 'UserAccount.IdValidationSuccessViewed',
        }}
      />
    )
  },
  [Steps.CPF_IN_USE]: ({ gA4EventHandlers }) => {
    gA4EventHandlers?.sendComponentRender({
      componentClass: null,
      componentName: COMPONENT_NAME.IDENTITY_VALIDATION,
      renders: COMPONENT_RENDER_EVENT_PROPS.CPF_IN_USE,
    })

    return <CPFInUse />
  },
  [Steps.REMOVAL_CPF_EMAIL]: ({ onClose, sharedData, viewer, eventData, gA4EventHandlers }) => {
    const old_user_id = sharedData?.profileInUse?.pid?.toString() ?? ''
    const stepEvent = (event: string, metadata: Record<string, string>) => {
      const pid = viewer?.pid.toString() || null

      sendMetrics(event, pid, {
        ...eventData,
        metadata: {
          cpf: sharedData?.cpf,
          birthDate: formatDate(sharedData?.birthDate?.getTime() || ''),
          old_user_id,
          ...eventData?.metadata,
          ...metadata,
        },
      })
    }
    gA4EventHandlers?.sendComponentRender({
      componentClass: null,
      componentName: COMPONENT_NAME.IDENTITY_ALREADY_VERIFIED,
      renders: COMPONENT_RENDER_EVENT_PROPS.REMOVAL_CPF_EMAIL,
    })

    return (
      <StaticStep
        testId="removal-cpf-email-step"
        imageSrc={emailImgSrc}
        title="E-mail de verificação enviado"
        bodyText={
          <>
            Acesse o e-mail que enviamos para <strong>{sharedData?.profileInUse?.email}</strong>{' '}
            para cadastrar o CPF à conta atual.
          </>
        }
        bodyText2={
          <>
            Entre em{' '}
            <Link
              href={SUPPORT_CONTACT_URL}
              target="_blank"
              onClick={() => {
                stepEvent('UserAccount.IdValidationChangeAccountSubmitted', {
                  button: 'SupportHelp',
                })
                gA4EventHandlers?.sendClick({
                  ...CLICK_EVENT_PROPS.REMOVAL_CPF_EMAIL,
                  selectedOption: SELECTED_OPTION.CONTACT_SUPPORT,
                })
              }}
            >
              contato com nosso suporte
            </Link>{' '}
            caso não reconheça este e-mail.
          </>
        }
        action={{
          onClick: () => {
            stepEvent('UserAccount.IdValidationChangeAccountSubmitted', { button: 'Finish' })
            gA4EventHandlers?.sendClick({
              ...CLICK_EVENT_PROPS.REMOVAL_CPF_EMAIL,
              selectedOption: SELECTED_OPTION.COMPLETE,
            })
            onClose?.()
          },
          label: 'Concluir',
        }}
        onLoadEvent={{
          name: 'UserAccount.IdValidationChangeAccountViewed',
          metadata: { old_user_id },
        }}
      />
    )
  },
  [Steps.REPLACING_CPF]: () => <ReplacingCPF />,
  [Steps.REPLACEMENT_SUCCESS]: ({ onClose, stepsCallback }) => (
    <StaticStep
      testId="replacement-success-step"
      imageSrc={successImgSrc}
      title="CPF vinculado"
      bodyText="Agora você pode continuar navegando pelo site."
      action={{
        onClick: () => {
          stepsCallback?.(true)
          onClose?.()
        },
        label: 'Continuar',
      }}
      onLoadEvent={{
        name: 'UserAccount.OverwriteCPFSuccessViewed',
      }}
    />
  ),
  [Steps.REPLACEMENT_ERROR]: () => (
    <StaticStep
      testId="replacement-error-step"
      imageSrc={errorImgSrc}
      title="Erro ao vincular CPF"
      bodyText="Não foi possível completar o fluxo. Tente novamente."
      action={{
        nextStep: Steps.VALIDATE_IDENTITY,
        label: 'Revisar Informações',
      }}
      onLoadEvent={{
        name: 'UserAccount.OverwriteCPFFailedViewed',
      }}
    />
  ),
}
